import { useQuery, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns/format';

import { QUERY_KEYS } from '@features/campaigns/performance/consts/queryKeys.ts';
import { generateAISummary } from '@features/campaigns/performance/services/generateAISummary';
import { getAISummary } from '@features/campaigns/performance/services/getAISummary.ts';
import { AISummaryType } from '@features/campaigns/performance/types/performance.ts';

interface UseAiSummaryProps {
  campaignId?: string;
  isUserAllowedToUseResource: boolean;
}

interface AiSummaryResults {
  triggerGenerateAiSummary: () => void;
  generatedSummary: string;
  generationDate: string;
  isSummaryGenerated: boolean;
  isSummaryLoading: boolean;
}

interface FormattedSummary {
  summary: string;
  generationDate: string;
}

const createAiSummaryQueryFn = (apiCall: (campaignId: string) => Promise<AISummaryType | null>) => {
  return async (campaignId?: string): Promise<FormattedSummary | null> => {
    if (!campaignId) {
      return null;
    }

    const results = await apiCall(campaignId);

    if (results === null) {
      return null;
    }

    return {
      summary: results.summary,
      generationDate: format(new Date(results.generationDate), 'dd/MM/yyyy HH:mm'),
    };
  };
};

interface ResultsFactoryParams {
  triggerGenerateAiSummary: () => void;
  summary: string;
  generationDate: string;
  isSummaryGenerated: boolean;
  isSummaryLoading: boolean;
}

const resultsFactory = ({
  triggerGenerateAiSummary,
  summary,
  generationDate,
  isSummaryGenerated,
  isSummaryLoading,
}: ResultsFactoryParams): AiSummaryResults => ({
  triggerGenerateAiSummary,
  generatedSummary: summary,
  generationDate,
  isSummaryGenerated,
  isSummaryLoading,
});

export function useAiSummary({ campaignId, isUserAllowedToUseResource }: UseAiSummaryProps): AiSummaryResults {
  const { data: fetchedSummary } = useQuery({
    queryKey: [QUERY_KEYS.AI_SUMMARY, campaignId],
    queryFn: () => createAiSummaryQueryFn(getAISummary)(campaignId),
    enabled: isUserAllowedToUseResource,
  });

  const {
    mutate: generatedSummaryMutation,
    isPending: isSummaryLoading,
    isSuccess: isMutationSuccess,
    data: mutationData,
  } = useMutation({
    mutationFn: () => createAiSummaryQueryFn(generateAISummary)(campaignId),
  });

  const triggerGenerateAiSummary = () => {
    if (campaignId) {
      generatedSummaryMutation();
    }
  };

  if (fetchedSummary) {
    return resultsFactory({
      triggerGenerateAiSummary,
      summary: fetchedSummary.summary,
      generationDate: fetchedSummary.generationDate,
      isSummaryGenerated: true,
      isSummaryLoading: false,
    });
  }

  if (isMutationSuccess && mutationData) {
    return resultsFactory({
      triggerGenerateAiSummary,
      summary: mutationData.summary,
      generationDate: mutationData.generationDate,
      isSummaryGenerated: true,
      isSummaryLoading: false,
    });
  }

  return resultsFactory({
    triggerGenerateAiSummary,
    summary: '',
    generationDate: '',
    isSummaryGenerated: false,
    isSummaryLoading: Boolean(isSummaryLoading),
  });
}
